import { useLayoutEffect, useState } from 'react'

import { fitToWidth } from 'src/utils'

type NodeRef = (node: HTMLDivElement | null) => void

type ActionOverflow = {
  roomRef: NodeRef
  barRef: NodeRef
  actionsRef: NodeRef
  measureRef: NodeRef
  overflowed: boolean[]
}

const sameFlags = (left: boolean[], right: boolean[]): boolean =>
  left.length === right.length &&
  left.every((flag, index) => flag === right[index])

export const useActionOverflow = (
  gap: number,
  inset: number,
  pinned: boolean[],
  chromeKey: number
): ActionOverflow => {
  const [room, roomRef] = useState<HTMLDivElement | null>(null)
  const [bar, barRef] = useState<HTMLDivElement | null>(null)
  const [actionsRow, actionsRef] = useState<HTMLDivElement | null>(null)
  const [measureRow, measureRef] = useState<HTMLDivElement | null>(null)
  const [overflowed, setOverflowed] = useState<boolean[]>([])

  const pinnedKey = pinned.join()

  useLayoutEffect(() => {
    if (
      !room ||
      !bar ||
      !actionsRow ||
      !measureRow ||
      typeof ResizeObserver === 'undefined'
    ) {
      return
    }

    const flags = pinnedKey
      ? pinnedKey.split(',').map((flag) => flag === 'true')
      : []

    const fit = () => {
      const roomWidth = room.clientWidth - inset * 2
      const barWidth = bar.offsetWidth

      if (roomWidth <= 0 || barWidth <= 0) {
        return
      }

      const widths = Array.from(measureRow.children, (child) =>
        Math.ceil(child.getBoundingClientRect().width)
      )
      const moreWidth = widths.length > flags.length ? (widths.pop() ?? 0) : 0
      const nonActionWidth = barWidth - actionsRow.offsetWidth

      const next = fitToWidth({
        widths,
        moreWidth,
        pinned: flags,
        gap,
        limit: roomWidth - nonActionWidth
      })

      setOverflowed((previous) => (sameFlags(previous, next) ? previous : next))
    }

    fit()

    const observer = new ResizeObserver(fit)

    observer.observe(room)
    observer.observe(measureRow)
    observer.observe(bar)
    observer.observe(actionsRow)

    return () => {
      observer.disconnect()
    }
  }, [room, bar, actionsRow, measureRow, pinnedKey, gap, inset, chromeKey])

  return { roomRef, barRef, actionsRef, measureRef, overflowed }
}
